import { useMemo, useState } from "react";
import { Skeleton } from "@/components/base/Loading";
import { formatNumber } from "@/lib/format";
import type { AdminProduct, ProductCategory } from "@/pages/admin/products/hooks/useAdminProducts";

interface ProductAdminListProps {
  products: AdminProduct[];
  categories: ProductCategory[];
  categoryFilter: string;
  onCategoryChange: (value: string) => void;
  loading: boolean;
  onEdit: (product: AdminProduct) => void;
  onToggleStatus: (product: AdminProduct) => void;
  onDelete: (product: AdminProduct) => void;
  onBatchStatus: (ids: number[], status: "active" | "inactive") => void;
}

type StatusFilter = "all" | "active" | "inactive" | "draft";

const STATUS_META: Record<string, { label: string; chip: string }> = {
  active: { label: "已上架", chip: "bg-primary-100 text-primary-700" },
  inactive: { label: "已下架", chip: "bg-secondary-100 text-secondary-900" },
  draft: { label: "草稿", chip: "bg-background-200 text-foreground-600" },
};

const FILTERS: { key: StatusFilter; label: string }[] = [
  { key: "all", label: "全部" },
  { key: "active", label: "已上架" },
  { key: "inactive", label: "已下架" },
  { key: "draft", label: "草稿" },
];

export default function ProductAdminList({
  products,
  categories,
  categoryFilter,
  onCategoryChange,
  loading,
  onEdit,
  onToggleStatus,
  onDelete,
  onBatchStatus,
}: ProductAdminListProps) {
  const [filter, setFilter] = useState<StatusFilter>("all");
  const [keyword, setKeyword] = useState("");
  const [confirmId, setConfirmId] = useState<number | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const categoryOptions = useMemo(() => {
    const set = new Set<string>();
    categories.forEach((item) => set.add(item.name));
    products.forEach((product) => {
      if (product.category) set.add(product.category);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, "zh-CN"));
  }, [categories, products]);

  const uncategorizedCount = useMemo(
    () => products.filter((product) => !product.category).length,
    [products],
  );

  const filtered = useMemo(() => {
    const term = keyword.trim().toLowerCase();
    return products.filter((product) => {
      if (filter !== "all" && product.status !== filter) return false;
      if (categoryFilter === "__none__") {
        if (product.category) return false;
      } else if (categoryFilter && product.category !== categoryFilter) {
        return false;
      }
      if (!term) return true;
      const haystack = `${product.name} ${product.category ?? ""} ${product.slug}`.toLowerCase();
      return haystack.includes(term);
    });
  }, [products, filter, categoryFilter, keyword]);

  const allFilteredSelected = filtered.length > 0 && filtered.every((product) => selected.has(product.id));

  const toggleOne = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filtered.forEach((product) => next.delete(product.id));
      } else {
        filtered.forEach((product) => next.add(product.id));
      }
      return next;
    });
  };

  const runBatch = (status: "active" | "inactive") => {
    if (selected.size === 0) return;
    onBatchStatus(Array.from(selected), status);
    setSelected(new Set());
  };

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-5">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h2 className="font-heading text-base font-semibold text-foreground-950">产品目录</h2>
          <p className="mt-1 text-xs text-foreground-500">
            管理可授权软件：维护版本历史与授权类型，一键上架或下架，也可勾选多款批量上下架。
          </p>
        </div>
        <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-secondary-700">
              <i className="ri-node-tree" />
            </span>
            <select
              value={categoryFilter}
              onChange={(event) => onCategoryChange(event.target.value)}
              aria-label="按分类筛选"
              className="w-full cursor-pointer appearance-none rounded-md border border-background-300 bg-background-50 py-2 pl-8 pr-8 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none sm:w-48"
            >
              <option value="">全部分类</option>
              {categoryOptions.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
              {uncategorizedCount > 0 && <option value="__none__">未分类（{uncategorizedCount}）</option>}
            </select>
            <span className="pointer-events-none absolute right-2.5 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
              <i className="ri-arrow-down-s-line" />
            </span>
          </div>
          <div className="relative w-full sm:w-56">
            <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
              <i className="ri-search-line" />
            </span>
            <input
              type="text"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索产品名称、分类或 slug"
              className="w-full rounded-md border border-background-300 bg-background-50 py-2 pl-9 pr-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
            />
          </div>
        </div>
      </div>

      {categoryFilter && (
        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-foreground-600">
          <span className="flex items-center gap-1.5">
            <span className="flex h-3.5 w-3.5 items-center justify-center text-secondary-700">
              <i className="ri-filter-3-line" />
            </span>
            已按分类筛选：
            <span className="rounded-full bg-secondary-100 px-2 py-0.5 font-medium text-secondary-900">
              {categoryFilter === "__none__" ? "未分类" : categoryFilter}
            </span>
          </span>
          <button
            type="button"
            onClick={() => onCategoryChange("")}
            className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-2.5 py-1 text-xs text-foreground-700 transition-colors hover:bg-background-100"
          >
            <span className="flex h-3 w-3 items-center justify-center">
              <i className="ri-close-line" />
            </span>
            清除分类筛选
          </button>
        </div>
      )}

      <div className="mt-4 inline-flex rounded-full border border-background-300 bg-background-100 p-1">
        {FILTERS.map((item) => {
          const active = item.key === filter;
          const count =
            item.key === "all"
              ? products.length
              : products.filter((product) => product.status === item.key).length;
          return (
            <button
              key={item.key}
              type="button"
              onClick={() => setFilter(item.key)}
              className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition-colors ${
                active ? "bg-background-50 text-foreground-950" : "text-foreground-600 hover:text-foreground-900"
              }`}
            >
              {item.label}
              <span className="text-xs text-foreground-400">{count}</span>
            </button>
          );
        })}
      </div>

      {!loading && filtered.length > 0 && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-md border border-background-200 bg-background-100 px-4 py-2.5">
          <button
            type="button"
            onClick={toggleAll}
            className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap text-sm text-foreground-800 transition-colors hover:text-primary-700"
          >
            <span className="flex h-4 w-4 items-center justify-center text-primary-600">
              <i className={allFilteredSelected ? "ri-checkbox-line" : "ri-checkbox-blank-line"} />
            </span>
            {allFilteredSelected ? "取消全选" : `全选当前 ${filtered.length} 款`}
          </button>

          {selected.size > 0 ? (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-foreground-600">
                已选 <strong className="text-foreground-950">{selected.size}</strong> 款
              </span>
              <button
                type="button"
                onClick={() => runBatch("active")}
                className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3 py-1.5 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <span className="flex h-3.5 w-3.5 items-center justify-center">
                  <i className="ri-upload-cloud-2-line" />
                </span>
                批量上架
              </button>
              <button
                type="button"
                onClick={() => runBatch("inactive")}
                className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs font-medium text-foreground-800 transition-colors hover:bg-background-100"
              >
                <span className="flex h-3.5 w-3.5 items-center justify-center text-foreground-600">
                  <i className="ri-eye-off-line" />
                </span>
                批量下架
              </button>
              <button
                type="button"
                onClick={() => setSelected(new Set())}
                className="cursor-pointer whitespace-nowrap rounded-md px-2 py-1.5 text-xs text-foreground-500 transition-colors hover:text-foreground-800"
              >
                清除选择
              </button>
            </div>
          ) : (
            <span className="text-xs text-foreground-400">勾选产品后可批量上架 / 下架</span>
          )}
        </div>
      )}

      {loading ? (
        <div className="mt-5 space-y-3">
          {[0, 1, 2].map((item) => (
            <Skeleton key={item} className="h-20 w-full" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="mt-5 flex flex-col items-center gap-2 rounded-lg border border-dashed border-background-300 py-12 text-center">
          <span className="flex h-11 w-11 items-center justify-center rounded-md bg-background-200 text-foreground-500">
            <i className="ri-apps-2-line text-xl" />
          </span>
          <p className="text-sm text-foreground-600">
            {products.length === 0 ? "还没有软件产品，点击右上角新增一款" : "没有符合条件的产品"}
          </p>
        </div>
      ) : (
        <ul className="mt-4 space-y-3">
          {filtered.map((product) => {
            const statusMeta = STATUS_META[product.status] ?? STATUS_META.draft;
            const isActive = product.status === "active";
            const versionCount = product.versions?.length ?? 0;
            const editionCount = product.editions?.length ?? 0;
            const checked = selected.has(product.id);
            return (
              <li
                key={product.id}
                className={`flex flex-col gap-3 rounded-lg border bg-background-100 p-4 lg:flex-row lg:items-center lg:justify-between ${
                  checked ? "border-primary-300" : "border-background-200"
                }`}
              >
                <div className="flex items-start gap-3">
                  <button
                    type="button"
                    onClick={() => toggleOne(product.id)}
                    aria-label={checked ? "取消选择" : "选择该产品"}
                    className={`mt-3 flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center rounded transition-colors ${
                      checked ? "text-primary-600" : "text-foreground-400 hover:text-foreground-600"
                    }`}
                  >
                    <i className={`${checked ? "ri-checkbox-line" : "ri-checkbox-blank-line"} text-lg`} />
                  </button>
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <i className={`${product.icon || "ri-apps-2-line"} text-xl`} />
                  </span>
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-medium text-foreground-950">{product.name}</p>
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${statusMeta.chip}`}>
                        {statusMeta.label}
                      </span>
                      {product.category && (
                        <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] text-secondary-900">
                          {product.category}
                        </span>
                      )}
                    </div>
                    <p className="mt-1 line-clamp-1 text-xs text-foreground-600">
                      {product.summary || "暂无简介"}
                    </p>
                    <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-500">
                      <span className="flex items-center gap-1">
                        <i className="ri-git-branch-line" />
                        {versionCount} 个版本
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-price-tag-3-line" />
                        {editionCount} 种授权
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-download-2-line" />
                        {formatNumber(product.downloads)} 次授权
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-star-fill text-accent-500" />
                        {Number(product.rating).toFixed(1)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2 lg:shrink-0">
                  <button
                    type="button"
                    onClick={() => onEdit(product)}
                    className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:bg-background-50"
                  >
                    <span className="flex h-4 w-4 items-center justify-center text-primary-700">
                      <i className="ri-edit-line" />
                    </span>
                    编辑
                  </button>
                  <button
                    type="button"
                    onClick={() => onToggleStatus(product)}
                    className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                      isActive
                        ? "border border-background-300 bg-background-50 text-foreground-800 hover:bg-background-50"
                        : "bg-primary-500 text-background-50 hover:bg-primary-600"
                    }`}
                  >
                    <span className="flex h-4 w-4 items-center justify-center">
                      <i className={isActive ? "ri-eye-off-line text-foreground-600" : "ri-upload-cloud-2-line"} />
                    </span>
                    {isActive ? "下架" : "上架"}
                  </button>

                  {confirmId === product.id ? (
                    <div className="inline-flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => {
                          onDelete(product);
                          setConfirmId(null);
                        }}
                        className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md bg-accent-600 px-3 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-accent-700"
                      >
                        确认删除
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmId(null)}
                        className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100"
                      >
                        取消
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setConfirmId(product.id)}
                      className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-600 transition-colors hover:border-accent-300 hover:bg-accent-50 hover:text-accent-700"
                      aria-label="删除产品"
                    >
                      <i className="ri-delete-bin-line" />
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}